import { useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Reveal } from "./Reveal";
import { CONDIZIONI, EXTRA_SERVIZI, POSE, POSE_IMG, eur, scrollToId } from "../lib/site";
import { Calculator as CalcIcon, Info } from "lucide-react";

const OPZIONI = [...POSE, ...EXTRA_SERVIZI];

const Calculator = ({ onEstimate }) => {
  const [mq, setMq] = useState(50);
  const [posa, setPosa] = useState("dritta");
  const [conds, setConds] = useState([]);

  const { min, max, sel } = useMemo(() => {
    const sel = OPZIONI.find((p) => p.id === posa);
    const base = sel.prezzo * mq;
    const extra = conds.reduce(
      (s, id) => s + CONDIZIONI.find((c) => c.id === id).extra,
      0
    );
    return { min: base, max: Math.round(base * (1 + extra)), sel };
  }, [mq, posa, conds]);

  const toggleCond = (id) =>
    setConds((c) => (c.includes(id) ? c.filter((x) => x !== id) : [...c, id]));

  const prefill = () => {
    onEstimate({ mq, tipo: posa, min, max });
    scrollToId("sopralluogo");
  };

  return (
    <section
      id="calcolatore"
      className="relative overflow-hidden bg-cream py-28 md:py-40"
      data-testid="calculator-section"
    >
      <AnimatePresence mode="popLayout">
        <motion.img
          key={posa}
          src={POSE_IMG[posa]}
          alt=""
          aria-hidden="true"
          initial={{ opacity: 0, scale: 1.04 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="pointer-events-none absolute inset-0 h-full w-full object-cover"
          data-testid="calc-bg-image"
        />
      </AnimatePresence>
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-cream via-cream/55 to-transparent" />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-cream to-transparent" />
      <div className="relative z-10 mx-auto grid max-w-7xl gap-14 px-5 md:px-10 lg:grid-cols-[1.1fr_1fr]">
        <Reveal>
          <p className="mb-4 flex items-center gap-2 font-sans text-xs font-light uppercase tracking-[0.45em] text-clay">
            <CalcIcon size={14} /> Calcolatore
          </p>
          <h2 className="font-display text-4xl font-medium leading-tight text-ink sm:text-5xl">
            Fai due conti, <em className="text-honey">al volo.</em>
          </h2>

          <div className="mt-12">
            <label className="font-sans text-sm font-medium text-bark">
              Metri quadri: <span data-testid="calc-mq-value">{mq} mq</span>
            </label>
            <input
              type="range"
              min="10"
              max="200"
              value={mq}
              onChange={(e) => setMq(Number(e.target.value))}
              data-testid="calc-mq-slider"
              className="mt-3 w-full accent-honey"
            />
          </div>

          <div className="mt-10 grid gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {OPZIONI.map((p) => (
              <button
                key={p.id}
                onClick={() => setPosa(p.id)}
                data-testid={`calc-posa-${p.id}`}
                className={`rounded-xl border p-4 text-left transition-[border-color,background-color,transform] duration-300 hover:-translate-y-0.5 ${
                  posa === p.id
                    ? "border-honey bg-walnut text-cream"
                    : "border-bark/20 bg-paper hover:border-honey/60"
                }`}
              >
                <span className="block font-display text-lg font-medium">
                  {p.nome}
                </span>
                <span
                  className={`mt-1 block font-sans text-xs ${
                    posa === p.id ? "text-honey" : "text-clay"
                  }`}
                >
                  {p.da ? "da " : ""}{p.prezzo}€ / mq
                </span>
              </button>
            ))}
          </div>

          <div className="mt-10 flex flex-wrap gap-3">
            {CONDIZIONI.map((c) => (
              <button
                key={c.id}
                onClick={() => toggleCond(c.id)}
                data-testid={`calc-cond-${c.id}`}
                className={`rounded-full border px-4 py-2 font-sans text-xs transition-colors duration-300 ${
                  conds.includes(c.id)
                    ? "border-honey bg-honey text-ink"
                    : "border-bark/25 text-bark hover:border-honey"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.15} className="lg:pt-24">
          <div className="grain relative overflow-hidden rounded-3xl bg-walnut p-9 text-cream shadow-2xl shadow-bark/40">
            <p className="font-sans text-xs font-light uppercase tracking-[0.4em] text-honey">
              Stima orientativa
            </p>
            <div className="mt-6 flex items-baseline gap-3 font-display">
              <span
                className="text-5xl font-semibold text-honey transition-opacity duration-300 md:text-6xl"
                data-testid="calc-estimate-min"
              >
                {sel.da ? "da " : ""}{eur(min)}
              </span>
              <span className="text-2xl text-sand/60">—</span>
              <span
                className="text-5xl font-semibold transition-opacity duration-300 md:text-6xl"
                data-testid="calc-estimate-max"
              >
                {eur(max)}
              </span>
            </div>
            <p
              className="mt-6 flex items-start gap-2 border-t border-cream/10 pt-5 font-sans text-xs font-light leading-relaxed text-sand/75"
              data-testid="calc-disclaimer"
            >
              <Info size={14} className="mt-0.5 shrink-0 text-honey" />
              Stima indicativa: il prezzo definitivo viene confermato dopo il
              sopralluogo gratuito, valutando le condizioni reali della casa.
            </p>
            <button
              onClick={prefill}
              data-testid="calc-cta-sopralluogo"
              className="mt-7 w-full rounded-full bg-honey py-3.5 font-sans text-sm font-medium text-ink transition-colors duration-300 hover:bg-cream"
            >
              Conferma con il sopralluogo gratuito
            </button>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default Calculator;
